import React, { useRef, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';

// Types
import { Props, BudgetItemManagement } from './types';

// Schema
import { BudgetItemManagementSchema } from './schema';

// Styles
import { Container, InputsContainer, AddBudgetItemButton } from './styles';

// Components
import InputText from '../../inputs/InputText';
import DropDownInput from '../../inputs/DropDown/DropDownInput';
import { Typography } from '../../display/Typography';

// Utils
import { useCacheFormInSessionStorage } from '../../../hooks/useCacheForm';
import { ImperativeRefInput } from '../../inputs/DropDown/DropDownInput/types';

export const AddBudgetItemForm = ({
  onFormSubmit,
  namingPersistForm,
  initialData,
  categories,
}: Props) => {
  const { t } = useTranslation(['forms', 'pages']);
  const [itemErrorMessage, setItemErrorMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    handleSubmit,
    control,
    register,
    formState: { errors },
    reset,
  } = useForm<BudgetItemManagement>({
    defaultValues: {
      ...initialData,
      isActive: true,
      isCreated: true,
    },
    resolver: yupResolver(BudgetItemManagementSchema) as any,
  });

  useCacheFormInSessionStorage(namingPersistForm, control);

  // Refs
  const categoryRef = useRef<ImperativeRefInput>(null);

  const onSubmit = async (data: BudgetItemManagement) => {
    if (isSubmitting) return; // Prevenir múltiples submits

    setIsSubmitting(true);
    try {
      const errorMessage = await onFormSubmit(data);
      if (errorMessage) {
        setItemErrorMessage(errorMessage);
      } else {
        reset({
          code: '',
          description: '',
          unit: '',
          unitPrice: 0,
          budgetCategoryId: '',
        });
        categoryRef.current?.clearSelected();
        setItemErrorMessage(null);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const categoryOptions =
    categories?.map((category) => ({
      label: `${category.code} - ${category.name}`,
      value: category.id,
    })) || [];

  return (
    <Container onSubmit={handleSubmit(onSubmit)}>
      <Typography size="L" variant="semiBold">
        {t('pages:management.addBudgetItem')}
      </Typography>

      <InputsContainer>
        <InputText
          {...register('code')}
          label={t('common:labels.code')}
          typeInput={'normal'}
          inputSize="XS"
          placeholder={t('placeholders.budgetItemCode')}
          borderError={!!errors.code?.message}
        />

        <InputText
          {...register('description')}
          label={t('common:labels.description')}
          typeInput={'normal'}
          inputSize="XL"
          placeholder={t('placeholders.budgetItemDescription')}
          borderError={!!errors.description?.message}
        />

        <Controller
          name="budgetCategoryId"
          control={control}
          render={({ field, formState: { defaultValues } }) => (
            <DropDownInput
              ref={categoryRef}
              typeDropDown="Default"
              labelText={t('common:labels.category')}
              placeholder={t('placeholders.select')}
              defaultValue={defaultValues?.budgetCategoryId}
              options={categoryOptions}
              onChangeSelected={(option) => field.onChange(option?.value ?? '')}
              borderError={!!errors.budgetCategoryId?.message}
              inputSize="M"
            />
          )}
        />

        <InputText
          {...register('unit')}
          label={t('common:labels.unit')}
          typeInput={'normal'}
          inputSize="XS"
          placeholder="Unitat"
          borderError={!!errors.unit?.message}
        />

        <InputText
          {...register('unitPrice')}
          label={t('common:labels.unitPrice')}
          typeInput="normal"
          type="number"
          inputSize="S"
          placeholder="0.00"
          borderError={!!errors.unitPrice?.message}
        />

        <AddBudgetItemButton type="submit" disabled={isSubmitting}>
          {isSubmitting ? '...' : '⮕'}
        </AddBudgetItemButton>

        {itemErrorMessage && (
          <Typography colorText="lightCoralRed" size="XS">
            {itemErrorMessage}
          </Typography>
        )}
      </InputsContainer>
    </Container>
  );
};

export default AddBudgetItemForm;
